/**
 * AdminToast — B 端後台 toast 通知（B.9 規格，唯一從零新寫）
 *
 * 載入：由 /src/admin.js 中央 import 鏈載入（後台 / auth / member 頁殼）；同時掛 window.AdminToast，
 *   供 member-favorites-tab / member-edit / admin-review-detail 等以全域或 import 兩路呼叫。auto-init container。
 *
 * API：
 *   import { AdminToast } from './components/admin-toast.js';
 *   AdminToast.show({ type: 'success', title: '已送出', text: '回覆已寄給諮詢者', duration: 4000 });
 *   // 或 window.AdminToast.show({...})
 *
 * 參數：
 *   type     — 'success' | 'warning' | 'danger' | 'info'（預設 info）→ 左色條 + a11y role
 *   title    — 粗體標題（必填，純文字）
 *   text     — 次行說明（選填，純文字）
 *   duration — 自動關閉毫秒（預設 4000；danger 預設 6000；傳 0 = 不自動關閉）
 *   action   — { label, onClick }（選填）→ 渲染 .admin-toast__action 鈕；點擊執行 onClick
 *              後以 skipFinalize 關閉（不觸發 onTimeout/onClose 定案語意）。undo「復原」用。
 *   countdown— boolean（選填）→ 渲染 .admin-toast__progress 倒數條（CSS scaleX 1→0，linear，
 *              duration = this.duration）；hover 與計時同步暫停（is-paused）。
 *   onTimeout— fn（選填）→ duration 到 / 點 × / ESC 關閉時觸發（undo 定案 = li.remove + empty-state）。
 *   onClose  — fn（選填）→ 同 onTimeout 時機的別名（兩者皆呼叫；任一存在即可）。
 *
 * 回傳：toast 實例，含 .focusAction()（焦點移到「復原」鈕，給鍵盤觸發場景）。
 *
 * 行為（B.9 + undo 擴充）：
 *   - 容器 fixed 右下，stack column-reverse（新的疊在下方靠近視線）
 *   - 進場 opacity+translateY、退場 opacity+translateX，token 化時長/easing
 *   - auto-dismiss，hover 暫停計時、leave 續算（剩餘時間）；倒數條同步暫停
 *   - reduced-motion：無 transition 直接顯示/移除，但計時保留；倒數條 CSS 隱藏
 *   - 關閉鈕重用 .modal__close 樣式，hit area ≥ 44（CSS 補）
 *   - ESC（toast 聚焦範圍內）：dismiss（= 定案語意，非復原）
 *   - 純文字插入用 textContent（非 innerHTML）→ 無 XSS 風險
 *   - action/countdown/onTimeout/onClose 為 opt-in，未傳則行為與既有完全一致（向後相容）
 */

const CONTAINER_ID = 'admin-toast-container';
const DEFAULT_DURATION = 4000;
const DANGER_DURATION = 6000;
const VALID_TYPES = ['success', 'warning', 'danger', 'info'];

const ROLE_BY_TYPE = {
  success: 'status',
  info: 'status',
  warning: 'alert',
  danger: 'alert',
};

// prefersReducedMotion 收進共用 util（原 inline 同式收斂，import 為 ESM hoisted）
import { prefersReducedMotion } from '../utils/dom.js';

function ensureContainer() {
  let container = document.getElementById(CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = CONTAINER_ID;
    container.className = 'admin-toast-stack';
    // 容器本身不攔指標事件，子 toast 各自 auto（避免蓋住下方頁面互動）
    container.setAttribute('aria-live', 'polite');
    document.body.appendChild(container);
  }
  return container;
}

class Toast {
  constructor({ type, title, text, duration, action, countdown, onTimeout, onClose }) {
    this.type = VALID_TYPES.includes(type) ? type : 'info';
    this.duration = typeof duration === 'number'
      ? duration
      : (this.type === 'danger' ? DANGER_DURATION : DEFAULT_DURATION);
    this.remaining = this.duration;
    this.timerId = null;
    this.startedAt = 0;
    this.action = action || null;
    this.countdown = !!countdown;
    this.onTimeout = typeof onTimeout === 'function' ? onTimeout : null;
    this.onClose = typeof onClose === 'function' ? onClose : null;
    this.actionBtn = null;
    this._build(title, text);
  }

  _build(title, text) {
    const el = document.createElement('div');
    el.className = `admin-toast admin-toast--${this.type}`;
    if (this.action || this.countdown) el.classList.add('admin-toast--undo');
    el.setAttribute('role', ROLE_BY_TYPE[this.type]);

    const body = document.createElement('div');
    body.className = 'admin-toast__body';

    const titleEl = document.createElement('p');
    titleEl.className = 'admin-toast__title';
    titleEl.textContent = title || '';
    this.titleEl = titleEl;
    body.appendChild(titleEl);

    if (text) {
      const textEl = document.createElement('p');
      textEl.className = 'admin-toast__text';
      textEl.textContent = text;
      body.appendChild(textEl);
    }

    el.appendChild(body);

    // action 鈕（「復原」）：點擊執行 onClick 後以 skipFinalize 關閉（復原不觸發定案）
    if (this.action && this.action.label) {
      const actionBtn = document.createElement('button');
      actionBtn.type = 'button';
      actionBtn.className = 'admin-toast__action';
      actionBtn.textContent = this.action.label;
      actionBtn.addEventListener('click', () => {
        if (typeof this.action.onClick === 'function') this.action.onClick();
        this.dismiss({ skipFinalize: true });
      });
      this.actionBtn = actionBtn;
      el.appendChild(actionBtn);
    }

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'modal__close admin-toast__close';
    closeBtn.setAttribute('aria-label', '關閉通知');
    closeBtn.textContent = '×'; // ×
    closeBtn.addEventListener('click', () => this.dismiss());
    el.appendChild(closeBtn);

    // 倒數條（純視覺，aria-hidden）：CSS scaleX 1→0，duration 由 CSS 變數帶入
    if (this.countdown && this.duration > 0) {
      el.style.setProperty('--admin-toast-duration', `${this.duration}ms`);
      const progress = document.createElement('div');
      progress.className = 'admin-toast__progress';
      progress.setAttribute('aria-hidden', 'true');
      el.appendChild(progress);
    }

    // hover 暫停 / leave 續算（計時 + 倒數條同步）
    el.addEventListener('mouseenter', () => this._pause());
    el.addEventListener('mouseleave', () => this._resume());

    // ESC：toast 聚焦範圍內按下 = dismiss（定案語意，非復原）
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.dismiss();
      }
    });

    this.el = el;
  }

  /** 焦點移到「復原」鈕（鍵盤觸發取消時用，避免焦點丟失） */
  focusAction() {
    if (this.actionBtn) this.actionBtn.focus();
  }

  mount(container) {
    container.appendChild(this.el);
    // 進場：下一幀加 is-visible 觸發 transition（reduced-motion 下 CSS 直接顯示）
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.el.classList.add('is-visible'));
    });
    this._startTimer();
    return this;
  }

  _startTimer() {
    if (this.duration <= 0) return; // 0 = 不自動關閉
    this.startedAt = Date.now();
    this.timerId = window.setTimeout(() => this.dismiss(), this.remaining);
  }

  _pause() {
    if (this.duration <= 0 || this.timerId === null) return;
    window.clearTimeout(this.timerId);
    this.timerId = null;
    this.remaining -= (Date.now() - this.startedAt);
    // 倒數條同步暫停（CSS animation-play-state）
    if (this.countdown) this.el.classList.add('is-paused');
  }

  _resume() {
    if (this.duration <= 0 || this.timerId !== null) return;
    if (this.countdown) this.el.classList.remove('is-paused');
    if (this.remaining <= 0) return this.dismiss();
    this._startTimer();
  }

  /**
   * @param {Object} [opts]
   * @param {boolean} [opts.skipFinalize] true = 復原路徑，不觸發 onTimeout/onClose 定案 callback。
   *        預設 false（逾時 / 點 × / ESC / flush）= 觸發定案 callback。
   */
  dismiss({ skipFinalize = false } = {}) {
    if (this._dismissing) return;
    this._dismissing = true;
    if (this.timerId !== null) {
      window.clearTimeout(this.timerId);
      this.timerId = null;
    }

    // 定案 callback（undo：li.remove + empty-state）；復原路徑跳過。先觸發再播退場。
    if (!skipFinalize) {
      if (this.onTimeout) this.onTimeout();
      if (this.onClose) this.onClose();
    }

    const remove = () => {
      if (this.el && this.el.parentNode) this.el.parentNode.removeChild(this.el);
    };

    if (prefersReducedMotion()) {
      remove(); // 計時保留、但移除無動畫
      return;
    }

    this.el.classList.remove('is-visible');
    this.el.classList.add('is-leaving');
    // 退場 transition 結束後移除（fallback timer 防 transitionend 不觸發）
    let done = false;
    const onEnd = () => {
      if (done) return;
      done = true;
      this.el.removeEventListener('transitionend', onEnd);
      remove();
    };
    this.el.addEventListener('transitionend', onEnd);
    window.setTimeout(onEnd, 400); // 對齊 --motion-base 上限 + 緩衝
  }
}

export const AdminToast = {
  show(opts = {}) {
    const container = ensureContainer();
    const toast = new Toast(opts);
    return toast.mount(container);
  },
};

if (typeof window !== 'undefined') {
  window.AdminToast = AdminToast;
}
